(function () {
  const SELECTOR = '[is="footer-parallax"]';
  const DEFAULT_START = "-30%";
  const DEFAULT_END = "0%";
  const DEFAULT_MIN_WIDTH = 960;
  const DEFAULT_MAX_WIDTH = Infinity;
  const DEFAULT_END_OFFSET = 0.5;

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const reduceMotion = () =>
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  function parseNumber(value, fallback) {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  }

  class FooterParallaxController {
    constructor(element) {
      this.element = element;
      this.animation = null;
      this.rafId = null;
      this.enabled = false;
      this.handleUpdate = this.requestUpdate.bind(this);
      this.handleResize = this.handleViewportResize.bind(this);
      this.viewportWidth = window.innerWidth || document.documentElement.clientWidth;
      this.viewportHeight = this.getViewportHeight();
      this.resizeObserver = window.ResizeObserver
        ? new ResizeObserver(this.handleUpdate)
        : null;

      this.setup();
    }

    get minWidth() {
      return parseNumber(
        this.element.dataset.parallaxMinWidth,
        DEFAULT_MIN_WIDTH,
      );
    }

    get maxWidth() {
      return parseNumber(
        this.element.dataset.parallaxMaxWidth,
        DEFAULT_MAX_WIDTH,
      );
    }

    get start() {
      return this.element.dataset.parallaxStart || DEFAULT_START;
    }

    get end() {
      return this.element.dataset.parallaxEnd || DEFAULT_END;
    }

    get distance() {
      const viewportHeight = this.viewportHeight || this.getViewportHeight();
      const endY = viewportHeight * clamp(this.endOffset, 0, 1);

      return parseNumber(
        this.element.dataset.parallaxDistance,
        viewportHeight - endY,
      );
    }

    getViewportHeight() {
      return window.innerHeight || document.documentElement.clientHeight;
    }

    get endOffset() {
      return parseNumber(
        this.element.dataset.parallaxEndOffset,
        DEFAULT_END_OFFSET,
      );
    }

    get canRun() {
      const width = window.innerWidth || document.documentElement.clientWidth;
      return (
        !reduceMotion() && width >= this.minWidth && width <= this.maxWidth
      );
    }

    setup() {
      if (!this.canRun) {
        this.disable();
        window.addEventListener("resize", this.handleResize, { passive: true });
        return;
      }

      this.enabled = true;
      this.element.style.willChange = "transform";

      if (typeof this.element.animate === "function") {
        this.animation = this.element.animate(
          [
            { transform: `translateY(${this.start})` },
            { transform: `translateY(${this.end})` },
          ],
          {
            duration: 1000,
            fill: "both",
            easing: "linear",
          },
        );
        this.animation.pause();
      }

      this.resizeObserver && this.resizeObserver.observe(this.element);
      window.addEventListener("scroll", this.handleUpdate, { passive: true });
      window.addEventListener("resize", this.handleResize, { passive: true });
      this.requestUpdate();
    }

    disable() {
      this.enabled = false;
      this.rafId && cancelAnimationFrame(this.rafId);
      this.rafId = null;
      this.animation && this.animation.cancel();
      this.animation = null;
      this.element.style.transform = "";
      this.element.style.willChange = "";
      this.resizeObserver && this.resizeObserver.disconnect();
      window.removeEventListener("scroll", this.handleUpdate);
      window.removeEventListener("resize", this.handleResize);
    }

    refresh() {
      this.disable();
      this.viewportWidth = window.innerWidth || document.documentElement.clientWidth;
      this.viewportHeight = this.getViewportHeight();
      this.setup();
    }

    handleViewportResize() {
      const width = window.innerWidth || document.documentElement.clientWidth;
      const height = this.getViewportHeight();
      const widthChanged = width !== this.viewportWidth;
      const heightDelta = Math.abs(height - this.viewportHeight);

      if (widthChanged || heightDelta > 120) {
        this.refresh();
        return;
      }

      this.requestUpdate();
    }

    requestUpdate() {
      if (!this.enabled || this.rafId) {
        return;
      }

      this.rafId = requestAnimationFrame(() => {
        this.rafId = null;
        this.update();
      });
    }

    update() {
      if (!this.canRun) {
        this.disable();
        window.addEventListener("resize", this.handleResize, { passive: true });
        return;
      }

      const viewportHeight = this.viewportHeight || this.getViewportHeight();
      const scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
      const distance = Math.max(1, this.distance);
      const progress = clamp(
        (scrollY + viewportHeight - this.getLayoutTop()) / distance,
        0,
        1,
      );

      if (this.animation) {
        this.animation.currentTime = progress * 1000;
      } else {
        this.element.style.transform = this.interpolateTransform(progress);
      }
    }

    getLayoutTop() {
      let top = 0;
      let node = this.element;

      while (node) {
        top += node.offsetTop || 0;
        node = node.offsetParent;
      }

      return top;
    }

    interpolateTransform(progress) {
      const start = parseFloat(this.start);
      const end = parseFloat(this.end);
      const unit =
        this.end.replace(String(end), "") ||
        this.start.replace(String(start), "") ||
        "%";

      if (!Number.isFinite(start) || !Number.isFinite(end)) {
        return progress < 1
          ? `translateY(${this.start})`
          : `translateY(${this.end})`;
      }

      return `translateY(${start + (end - start) * progress}${unit})`;
    }
  }

  function initFooterParallax(root) {
    const scope = root && root.querySelectorAll ? root : document;
    const elements = [];

    if (scope.matches && scope.matches(SELECTOR)) {
      elements.push(scope);
    }

    scope
      .querySelectorAll(SELECTOR)
      .forEach((element) => elements.push(element));

    elements.forEach((element) => {
      if (element.__footerParallaxController) {
        element.__footerParallaxController.refresh();
        return;
      }

      element.__footerParallaxController = new FooterParallaxController(
        element,
      );
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      () => initFooterParallax(document),
      { once: true },
    );
  } else {
    initFooterParallax(document);
  }

  document.addEventListener("shopify:section:load", (event) =>
    initFooterParallax(event.target),
  );

  window.PatPatInitFooterParallax = initFooterParallax;
})();
